import { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { status } from 'constants/general';
import Button from 'modules/common/components/Button';
import Popup from 'modules/common/components/Popup';
import Loader from 'modules/common/components/Loader';
import ConfirmationModal from 'modules/common/components/ConfirmationModal';
import categoriesStrings from 'modules/categories/strings';
import {
  fetchRelatedPriceLists,
  resetRelatedPriceLists,
  removeProvider,
} from 'modules/categories/categoriesSlice';
import { selectedHealthPlanCountrySelector } from 'modules/healthPlans/healthPlansSlice';
import {
  buildRelatedPriceListsQuery,
  getProviderRecordFromPriceList,
} from 'modules/modals/RelatedPriceListsModal/helper';
import {
  PopupTitleSpan,
  PopupWrapper,
  PopupTitle,
  PopupTitleWrapper,
  Container,
  PopupTitleContainer,
  extendLoaderStyle,
  ButtonsContainer,
  extendGrayButtonStyle,
  extendSmallRedButtonStyle,
  ItemsContainer,
  ItemContainer,
  ItemWrapper,
  EmptyState,
  LoadingWrapper,
  Subtitle,
} from 'modules/modals/RelatedPriceListsModal/RelatedPriceListsModal.style';

const DoctorPriceListsModal = ({
  onClose,
  providerKey,
  providerName,
  categoryId,
  beneficiaryCompanyKey,
  currentPriceListId,
  currentProviderRecordId,
}) => {
  const dispatch = useDispatch();
  const relatedPriceLists = useSelector(state => state.categories.relatedPriceLists);
  const removeProviderState = useSelector(state => state.categories.removeProvider);
  const selectedHealthPlanCountry = useSelector(selectedHealthPlanCountrySelector);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [isDeleteConfirmationHidden, setIsDeleteConfirmationHidden] = useState(true);

  const currencyName = selectedHealthPlanCountry?.currency?.name || '';
  const isLoading = relatedPriceLists.status === status.FETCHING;
  const prevRemoveProviderState = useRef(removeProviderState.status);

  const loadPriceLists = () => {
    dispatch(
      fetchRelatedPriceLists(
        buildRelatedPriceListsQuery({
          categoryId,
          beneficiaryCompanyKey,
          providerKey,
        }),
      ),
    );
  };

  useEffect(() => {
    loadPriceLists();

    return () => {
      dispatch(resetRelatedPriceLists());
    };
  }, [dispatch, categoryId, beneficiaryCompanyKey, providerKey]);

  useEffect(() => {
    if (
      removeProviderState.status === status.SUCCESS &&
      prevRemoveProviderState.current !== removeProviderState.status
    ) {
      const deletedFromCurrentList =
        pendingDelete?.priceListId === Number(currentPriceListId);

      setIsDeleteConfirmationHidden(true);
      setPendingDelete(null);

      if (deletedFromCurrentList) {
        handleClose();
        return;
      }

      loadPriceLists();
    }
    prevRemoveProviderState.current = removeProviderState.status;
  }, [removeProviderState.status]);

  const handleClose = () => {
    dispatch(resetRelatedPriceLists());
    onClose();
  };

  const resolveProviderRecordId = priceList => {
    if (priceList.id === Number(currentPriceListId) && currentProviderRecordId) {
      return currentProviderRecordId;
    }

    return getProviderRecordFromPriceList(priceList, providerKey)?.id;
  };

  const handleDeleteClick = priceList => {
    const providerRecordId = resolveProviderRecordId(priceList);

    if (!providerRecordId) return;

    setPendingDelete({
      providerRecordId,
      priceListId: priceList.id,
      healthPlanName: priceList.healthPlan?.name,
      priceListName: priceList.name,
    });
    setIsDeleteConfirmationHidden(false);
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;

    dispatch(
      removeProvider({
        id: pendingDelete.providerRecordId,
        data: {
          priceListId: pendingDelete.priceListId,
        },
      }),
    );
  };

  const cancelDelete = () => {
    setIsDeleteConfirmationHidden(true);
    setPendingDelete(null);
  };

  const priceLists = relatedPriceLists.data || [];

  return (
    <>
      <Popup showCloseBtn={false}>
        <PopupWrapper>
          <PopupTitleContainer>
            <PopupTitleWrapper>
              <PopupTitle>{categoriesStrings.doctorPriceListsModal.title}</PopupTitle>
              <PopupTitleSpan onClick={handleClose} />
            </PopupTitleWrapper>
          </PopupTitleContainer>

          <Container>
            <Subtitle>
              {categoriesStrings.doctorPriceListsModal.subtitle.replace('{name}', providerName)}
            </Subtitle>

            {isLoading && (
              <LoadingWrapper>
                <Loader extendStyle={extendLoaderStyle} />
              </LoadingWrapper>
            )}

            {!isLoading && priceLists.length === 0 && (
              <EmptyState>{categoriesStrings.doctorPriceListsModal.emptyState}</EmptyState>
            )}

            {!isLoading && priceLists.length > 0 && (
              <ItemsContainer>
                <ItemContainer>
                  <ItemWrapper>{categoriesStrings.doctorPriceListsModal.healthPlan}</ItemWrapper>
                  <ItemWrapper>{categoriesStrings.doctorPriceListsModal.priceList}</ItemWrapper>
                  <ItemWrapper>{categoriesStrings.doctorPriceListsModal.consultationFee}</ItemWrapper>
                  <ItemWrapper>{categoriesStrings.doctorPriceListsModal.action}</ItemWrapper>
                </ItemContainer>
                {priceLists.map(priceList => {
                  const fee = priceList.priceListConfigurations?.[0]?.priceValue;
                  const providerRecordId = resolveProviderRecordId(priceList);
                  const canDelete = !!providerRecordId;

                  return (
                    <ItemContainer key={priceList.id}>
                      <ItemWrapper>{priceList.healthPlan?.name}</ItemWrapper>
                      <ItemWrapper>{priceList.name}</ItemWrapper>
                      <ItemWrapper>
                        {fee} {currencyName}
                      </ItemWrapper>
                      <ItemWrapper>
                        <Button
                          btnText={categoriesStrings.doctorPriceListsModal.remove}
                          onClick={() => handleDeleteClick(priceList)}
                          extendButtonStyle={extendSmallRedButtonStyle}
                          disabled={!canDelete}
                          isLoading={
                            removeProviderState.status === status.SUBMITING &&
                            pendingDelete?.priceListId === priceList.id
                          }
                          extendLoaderStyle={extendLoaderStyle}
                        />
                      </ItemWrapper>
                    </ItemContainer>
                  );
                })}
              </ItemsContainer>
            )}

            <ButtonsContainer>
              <Button
                onClick={handleClose}
                btnText={categoriesStrings.doctorPriceListsModal.close}
                extendButtonStyle={extendGrayButtonStyle}
              />
            </ButtonsContainer>
          </Container>
        </PopupWrapper>
      </Popup>

      <ConfirmationModal
        isModalHidden={isDeleteConfirmationHidden}
        onCloseModal={cancelDelete}
        onConfirm={confirmDelete}
        title={categoriesStrings.doctorPriceListsModal.confirmTitle}
        message={categoriesStrings.doctorPriceListsModal.confirmMessage
          .replace('{name}', providerName)
          .replace('{plan}', pendingDelete?.healthPlanName || '')
          .replace('{priceList}', pendingDelete?.priceListName || '')}
        confirmText={categoriesStrings.doctorPriceListsModal.remove}
        cancelText={categoriesStrings.priceLists.cancel}
        confirmButtonColor="#DC3545"
        isLoading={removeProviderState.status === status.SUBMITING}
      />
    </>
  );
};

DoctorPriceListsModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  providerKey: PropTypes.string.isRequired,
  providerName: PropTypes.string.isRequired,
  categoryId: PropTypes.number.isRequired,
  beneficiaryCompanyKey: PropTypes.string,
  currentPriceListId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  currentProviderRecordId: PropTypes.number,
};

DoctorPriceListsModal.defaultProps = {
  beneficiaryCompanyKey: null,
  currentProviderRecordId: null,
};

export default DoctorPriceListsModal;
